import React from "react";
import { createRoot } from "react-dom/client";
import "~/style.css";
import { Logger } from "~/utils/logger";

const logger = new Logger("frontend", "info");

const getWidgetProps = (el: Element) => {
  // we serialize the widget settings as JSON in a hidden pre tag when we
  // render the widget in PHP, so we can easily access all the settings as
  // props in our React component.  This is a common pattern for passing
  // data from PHP to JS in WordPress.
  const data = JSON.parse(el?.querySelector("pre")?.innerText ?? "{}");
  return data;
};

const getWidgetStyleName = (el: Element) => {
  const widgetId = el.getAttribute("widget-id");
  return `cn-widget-style-${widgetId}`;
};

const updateWidgetStyles = (el: Element, width: number, height: number) => {
  logger.debug(
    `Updating style for widget with width: ${width} and height: ${height} `,
  );

  const styleName = getWidgetStyleName(el);

  // we create a style element with a unique id for this widget instance and set the styles to
  // match the current size of the widget in the editor.  It also sets the
  // visibility of our placeholder element to hidden until we've rendered our React component.
  // This is to avoid repaints and layout shifts when the widget is updated in the editor.
  const innerText = `.${styleName} { visibility: hidden; width: ${width}px; height: ${height}px; }`;

  const style = document.getElementById(styleName);
  if (!style) {
    // first time, create a new style element and append it to the head
    const style = document.createElement("style");
    style.id = styleName;
    style.innerText = innerText;
    document.head.appendChild(style);
    return;
  }

  // already exists, just update the styles
  style.innerText = innerText;
};

const insertReactComponent = (
  el: HTMLElement,
  widgetClassName: string,
  Component: React.ComponentType<any>,
) => {
  try {
    logger.debug(`Inserting React component for widget ${widgetClassName} `);
    const props = getWidgetProps(el);
    const styleName = getWidgetStyleName(el);

    // logger.debug("Parsed data for React component: ", props);

    // create a child eleement to render the React component into, we can cache
    // it for subseuent renders to avoid recreating the component every time
    const reactRootEl = document.createElement("div");
    reactRootEl.classList.add("react-root");

    const root = createRoot(reactRootEl);
    root.render(<Component {...props} />);
    el.classList.remove(widgetClassName);
    el.appendChild(reactRootEl);
    el.classList.remove(styleName); // remove the style that sets opacity to 0 after we've rendered the component, so it becomes visible

    return { reactRootEl, root };
  } catch (error) {
    logger.error("Error parsing data for React component: ", error);
  }

  return { reactRootEl: null, root: null };
};

const updateReactComponent = (
  el: HTMLElement,
  reactRootEl: Element,
  root: any,
  widgetClassName: string,
  Component: React.ComponentType<any>,
) => {
  try {
    logger.info(
      `Re-rendering React component for widget ${widgetClassName} `,
      el,
    );
    const props = getWidgetProps(el);
    const styleName = getWidgetStyleName(el);
    // logger.debug("Parsed data for React component: ", props);

    root.render(<Component {...props} />);

    el.classList.remove(widgetClassName);
    el.appendChild(reactRootEl);
    el.classList.remove(styleName); // remove the style after we've rendered the component, so it becomes visible
  } catch (error) {
    logger.error("Error parsing data for React component: ", error);
  }
};

// in the editor environment, we keep track of the widgets and re-render them vs.
// recreating them each time.  this improves the performance when editing a widget
// since we don't have to recreate the component every time the user
// makes a change in the editor
type EditWidget = {
  reactRootEl: HTMLDivElement;
  root: any;
};
const editWidgets: Record<string, EditWidget> = {};

const updateEditorWidget = (
  el: HTMLElement,
  widgetName: string,
  editComponent: React.ComponentType<any>,
) => {
  const widgetId = el.getAttribute("widget-id");
  logger.info("in edit environment, widget id: ", widgetId);
  logger.debug("editWidgets: ", editWidgets);

  if (!widgetId) {
    logger.error(
      `Could not find widget-id attribute on element ${el} to update React component`,
    );
    return;
  }

  if (!editWidgets[widgetId]) {
    // not found, create a new component instance and store the root and element
    const { reactRootEl, root } = insertReactComponent(
      el,
      widgetName,
      editComponent,
    );

    if (!reactRootEl || !root) {
      logger.error(
        `Failed to initialize React component for widget ${widgetName} with id ${widgetId}`,
      );
      return;
    }

    editWidgets[widgetId] = {
      reactRootEl,
      root,
    };
  } else {
    // re-parent and re-render existing component instance with new props
    logger.info(`Widget with id ${widgetId} already initialized`);
    const { reactRootEl, root } = editWidgets[widgetId];
    updateReactComponent(el, reactRootEl, root, widgetName, editComponent);
  }
};

const attachReactComponent = (
  widgetName: string,
  viewComponent: React.ComponentType<any>,
  editComponent: React.ComponentType<any>,
  el: HTMLElement,
  isEdit: boolean,
) => {
  if (isEdit) {
    // listen for size updates on our widget element so we can update the styles
    // of our placeholder element to match the current size of the widget in the editor.
    // this ensures that when a user resizes the widget in the editor, the placeholder
    // element will also resize to match it, providing less layout shift.
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        // Get new dimensions
        const width = entry.contentRect.width;
        const height = entry.contentRect.height;

        if (width === 0 && height === 0) {
          logger.warn(
            `ResizeObserver: widget element has zero width and height, skipping style update`,
          );
          return;
        }

        logger.debug(`Element ${el} resized to: ${width}px x ${height}px`);
        updateWidgetStyles(el, width, height);
      }
    });

    resizeObserver.observe(el);

    // in edit mode we want to keep track of the root and element so
    // we can unmount the component when the widget is removed or
    // switched to a different widget
    updateEditorWidget(el, widgetName, editComponent);
  } else {
    insertReactComponent(el, widgetName, viewComponent);
  }
};

export const initializeReactWidget = (
  widgetName: string,
  viewComponent: React.ComponentType<any>,
  editComponent: React.ComponentType<any>,
) => {
  logger.info("Initializing react component for widget: ", widgetName);

  // this will wait to load both the class as well as the handler until
  // after the frontend is initialized.  If the class is defined outside
  // of this listener, the elementorModules may not be available yet and the class definition will fail
  window.addEventListener("elementor/frontend/init", () => {
    class ElementHandlerClass extends elementorModules.frontend.handlers.Base {
      onInit() {
        super.onInit();

        // the widget class name is used to identify elements to attach our React component to
        // the php widget file uses this same value when rendering the widget container
        logger.debug(`onInit: initializing ${widgetName}`, this);

        const el = this.$element.find(`.${widgetName}`)[0];
        logger.debug("onInit: Element for widget: ", el);
        if (!el) {
          logger.error(
            `Could not find element with class ${widgetName} to attach React component`,
          );
          return;
        }

        attachReactComponent(
          widgetName,
          viewComponent,
          editComponent,
          el,
          this.isEdit,
        );
      }

      // bindEvents() {}

      // getDefaultSettings() {
      //   logger.debug(`getDefaultSettings: for ${widgetName} widget `, this);
      // }

      // onElementChange() {
      //   logger.debug(`onElementChange: widget changed`, this);
      // }

      // onEditSettingsChange() {
      //   logger.debug(
      //     `onEditSettingsChange: ${widgetName} widget settings changed`,
      //     this,
      //   );
      // }

      // onDestroy() {
      //   super.onDestroy();

      //   logger.debug(`onDestroy: ${widgetName} widget destroyed`, this);
      // }
    }

    logger.debug(
      `Elementor frontend initialized for ${widgetName}`,
      elementorFrontend,
    );

    // this is the new, recommended way to attach a handler to an elementor widget.
    // https://developers.elementor.com/a-new-method-for-attaching-a-js-handler-to-an-elementor-widget/
    elementorFrontend.elementsHandler.attachHandler(
      widgetName,
      ElementHandlerClass,
    );
  });
};
